import {
  postJsMessage,
  unregisterSpecificNotificationHandler,
  registerSpecificNotificationHandler,
  isInWebview,
} from '../../bridge';
import API from '../../api';
import { Platform } from '../../utils/platform';
import { isAvailable } from '../utils';
import { NativeAPIResponseMessage } from '../../utils/type';
import { PostMessageOption, NotificationHandler } from '../../types';
import {
  addShortcutListener,
  removeShortcutListener,
  queryHotkey,
  onHotkeyComboChange,
  offHotkeyComboChange,
} from './shortcut';
export { ShortcutKeyName } from './shortcut';
import {
  enableHeartbeat,
  disableHeartbeat,
  getHeartbeatStatus,
} from './heartbeat';
export type * from './heartbeat';
import type {
  VisibilityState,
  sendMessage,
  WebviewMessageData,
  ReplyNotification,
  WebviewData,
  ResizeRect,
  WebviewRect,
  onPageEnterForegroundCallback,
  onPageEnterBackgroundCallback,
  OnWebviewZoomFactorChangeCallback,
  OnWebviewPushNotification,
} from './types';

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * webview.closeWebview
 *
 * [PLATFORM]
 * mac        NotSupport
 * windows    NotSupport
 * linux      NotSupport
 * ios        5.17.0+
 * android    5.17.0+
 * -------------------------------------------------- API Doc --------------------------------------------------
 */
export function closeWebview(clearCache: boolean): Promise<any> {
  return isAvailable('webview.closeWebview').then(() => {
    return postJsMessage(API.closeWebview, { clearCache: !!clearCache });
  });
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * webview.getWebviewId
 *
 * [PLATFORM]
 * mac        6.4.0
 * windows    6.4.0
 * linux      6.4.0
 * ios        6.4.0
 * android    6.4.0
 * -------------------------------------------------- API Doc --------------------------------------------------
 */
export function getWebviewId(): Promise<
  NativeAPIResponseMessage<{ webviewId: string }>
> {
  return isAvailable('webview.getWebviewId').then(() => {
    return postJsMessage(API.getWebviewId, {});
  });
}

const visibilityHandlerMap = new Map();
/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * webview.onVisibilityChange
 *
 * [PLATFORM]
 * mac        6.0.5+
 * windows    6.0.5+
 * linux      6.0.5+
 * ios        6.0.5+
 * android    6.0.5+
 * -------------------------------------------------- API Doc --------------------------------------------------
 */
export async function onVisibilityChange(
  visibilityChangeCallback: (visibilityState: VisibilityState) => void
) {
  await isAvailable('webview.onVisibilityChange');
  if (
    Platform.isMacOS() ||
    Platform.isWindows() ||
    Platform.isIOS() ||
    Platform.isAndroid()
  ) {
    const wrapperHandler = () => {
      visibilityChangeCallback(document.visibilityState);
    };
    visibilityHandlerMap.set(visibilityChangeCallback, wrapperHandler);
    document.addEventListener('visibilitychange', wrapperHandler);
  } else {
    return isAvailable('webview.onVisibilityChange').then(() => {
      const wrapperHandler = (
        result: NativeAPIResponseMessage<{ visibilityState: VisibilityState }>
      ) => {
        if (result.returnCode === 0) {
          visibilityChangeCallback(result.result.visibilityState);
        }
      };
      visibilityHandlerMap.set(visibilityChangeCallback, wrapperHandler);
      registerSpecificNotificationHandler(
        'UnifyNotif_OnWebviewVisibilityChange',
        wrapperHandler
      );
    });
  }
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * webview.offVisibilityChange
 *
 * [PLATFORM]
 * mac        6.0.5+
 * windows    6.0.5+
 * linux      6.0.5+
 * ios        NotSupport
 * android    NotSupport
 * -------------------------------------------------- API Doc --------------------------------------------------
 */
export async function offVisibilityChange(
  visibilityChangeCallback: (visibilityState: VisibilityState) => void
) {
  await isAvailable('webview.offVisibilityChange');
  if (Platform.isMacOS() || Platform.isWindows()) {
    if (visibilityHandlerMap.has(visibilityChangeCallback)) {
      document.removeEventListener(
        'visibilitychange',
        visibilityHandlerMap.get(visibilityChangeCallback)
      );
      visibilityHandlerMap.delete(visibilityChangeCallback);
    }
  } else {
    if (visibilityHandlerMap.has(visibilityChangeCallback)) {
      unregisterSpecificNotificationHandler(
        'UnifyNotif_OnWebviewVisibilityChange',
        visibilityHandlerMap.get(visibilityChangeCallback)
      );
      visibilityHandlerMap.delete(visibilityChangeCallback);
    }
  }
}

const ReceiveMessageNotification = 'UnifyNotif_OnReceiveMessage';
/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * webview.send
 *
 * [PLATFORM]
 * mac        6.2.5+
 * windows    6.2.5+
 * linux      6.2.5+
 * ios        6.2.5+
 * android    6.2.5+
 * -------------------------------------------------- API Doc --------------------------------------------------
 */
export async function send<T = any>(
  data: sendMessage<T>,
  options?: PostMessageOption
) {
  await isAvailable('webview.send');
  return postJsMessage(API.sendMessage, { ...data }, options);
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * webview.onReceiveMessage
 *
 * [PLATFORM]
 * mac        6.2.5+
 * windows    6.2.5+
 * linux      6.2.5+
 * ios        6.2.5+
 * android    6.2.5+
 * -------------------------------------------------- API Doc --------------------------------------------------
 */
export async function onReceiveMessage<T = any>(
  onReceiveMessageCallback: NotificationHandler<WebviewMessageData<T>>
) {
  await isAvailable('webview.onReceiveMessage');
  registerSpecificNotificationHandler(
    ReceiveMessageNotification,
    onReceiveMessageCallback
  );
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * webview.offReceiveMessage
 *
 * [PLATFORM]
 * mac        6.2.5+
 * windows    6.2.5+
 * linux      6.2.5+
 * ios        6.2.5+
 * android    6.2.5+
 * -------------------------------------------------- API Doc --------------------------------------------------
 */
export async function offReceiveMessage<T = any>(
  onReceiveMessageCallback: NotificationHandler<WebviewMessageData<T>>
) {
  await isAvailable('webview.offReceiveMessage');
  unregisterSpecificNotificationHandler(
    ReceiveMessageNotification,
    onReceiveMessageCallback
  );
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * webview.replyNotification
 *
 * [PLATFORM]
 * mac        6.2.5+
 * windows    6.2.5+
 * linux      6.2.5+
 * ios        6.2.5+
 * android    6.2.5+
 * -------------------------------------------------- API Doc --------------------------------------------------
 */
export function replyNotification(
  params: ReplyNotification,
  options?: PostMessageOption
): Promise<NativeAPIResponseMessage<any>> {
  return isAvailable('webview.replyNotification').then(() => {
    return postJsMessage(API.replyNotification, { ...params }, options);
  });
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * webview.getPendingRequests
 *
 * [PLATFORM]
 * mac        6.4.10+
 * windows    6.4.10+
 * linux      6.4.10+
 * ios        6.4.10+
 * android    6.4.10+
 * -------------------------------------------------- API Doc --------------------------------------------------
 */
export async function getPendingRequests(
  options?: PostMessageOption
): Promise<NativeAPIResponseMessage<{ pendingRequestIdList: string[] }>> {
  await isAvailable('webview.getPendingRequests');
  return postJsMessage(API.getPendingRequests, {}, options);
}

const WebviewWillRecycleNotification = 'UnifyNotif_OnWebviewWillRecycle';

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * webview.onWebviewWillRecycle
 *
 * [PLATFORM]
 * mac        6.4.0+
 * windows    6.4.0+
 * linux      6.4.0+
 * ios        NotSupport
 * android    NotSupport
 * -------------------------------------------------- API Doc --------------------------------------------------
 */
export async function onWebviewWillRecycle<T = any>(
  onWebviewWillRecycleCallback: NotificationHandler<T>
) {
  await isAvailable('webview.onWebviewWillRecycle');
  registerSpecificNotificationHandler(
    WebviewWillRecycleNotification,
    onWebviewWillRecycleCallback
  );
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * webview.offWebviewWillRecycle
 *
 * [PLATFORM]
 * mac        6.3.0+
 * windows    6.3.0+
 * linux      6.3.0+
 * ios        NotSupport
 * android    NotSupport
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export async function offWebviewWillRecycle<T = any>(
  onWebviewWillRecycleCallback: NotificationHandler<T>
) {
  await isAvailable('webview.offWebviewWillRecycle');
  unregisterSpecificNotificationHandler(
    WebviewWillRecycleNotification,
    onWebviewWillRecycleCallback
  );
}

const WebviewLostFocusNotification = 'UnifyNotify_OnWebviewLostFocus';

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * webview.onWebviewLostFocus
 *
 * [PLATFORM]
 * mac        6.3.0+
 * windows    6.3.0+
 * linux      6.3.0+
 * ios        NotSupport
 * android    NotSupport
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export async function onWebviewLostFocus<T = any>(
  onWebviewLostFocusCallback: NotificationHandler<T>
) {
  await isAvailable('webview.onWebviewLostFocus');
  registerSpecificNotificationHandler(
    WebviewLostFocusNotification,
    onWebviewLostFocusCallback
  );
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * webview.offWebviewLostFocus
 *
 * [PLATFORM]
 * mac        6.3.0+
 * windows    6.3.0+
 * linux      6.3.0+
 * ios        NotSupport
 * android    NotSupport
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export async function offWebviewLostFocus<T = any>(
  onWebviewLostFocusCallback: NotificationHandler<T>
) {
  await isAvailable('webview.offWebviewLostFocus');
  unregisterSpecificNotificationHandler(
    WebviewLostFocusNotification,
    onWebviewLostFocusCallback
  );
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * webview.saveWebContent
 *
 * [PLATFORM]
 * mac        6.3.0+
 * windows    6.3.0+
 * linux      6.3.0+
 * ios        NotSupport
 * android    NotSupport
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export function saveWebContent(
  params: WebviewData
): Promise<NativeAPIResponseMessage<any>> {
  return isAvailable('webview.saveWebContent').then(() => {
    return postJsMessage(API.saveWebContent, { ...params });
  });
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * webview.restoreWebContent
 *
 * [PLATFORM]
 * mac        6.3.0+
 * windows    6.3.0+
 * linux      6.3.0+
 * ios        NotSupport
 * android    NotSupport
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export function restoreWebContent(
  options?: PostMessageOption
): Promise<NativeAPIResponseMessage<{ data: string }>> {
  return isAvailable('webview.restoreWebContent').then(() => {
    return postJsMessage(
      API.restoreWebContent,
      {},
      { timeout: 60, ...options, needResponse: true }
    );
  });
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * webview.resize
 *
 * [PLATFORM]
 * mac        6.3.0+
 * windows    6.3.0+
 * linux      6.3.0+
 * ios        notSupport
 * android    notSupport
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export async function resize(rect: ResizeRect, options?: PostMessageOption) {
  await isAvailable('webview.resize');
  return postJsMessage(API.webviewResize, rect, options);
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * webview.getRect
 *
 * [PLATFORM]
 * mac        6.5.0+
 * windows    6.5.0+
 * linux      6.5.0+
 * ios        notSupport
 * android    notSupport
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export async function getRect(options?: PostMessageOption) {
  await isAvailable('webview.getRect');
  return postJsMessage<{}, WebviewRect>(API.webviewGetRect, {}, options);
}

const PageEnterForegroundNotification = [
  'UnifyNotify_OnPageEnterForeground',
  'UnifyNotif_OnPageEnterForeground',
];

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * webview.onPageEnterForeground

 * [PLATFORM]
 * mac        NotSupport
 * windows    NotSupport
 * linux      NotSupport
 * ios        6.4.0+
 * android    6.4.0+
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export async function onPageEnterForeground(
  onPageEnterForegroundCallback: onPageEnterForegroundCallback
) {
  await isAvailable('webview.onPageEnterForeground');
  registerSpecificNotificationHandler(
    PageEnterForegroundNotification,
    onPageEnterForegroundCallback
  );
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * webview.offPageEnterForeground
 *
 * [PLATFORM]
 * mac        6.3.0+
 * windows    6.3.0+
 * linux      6.3.0+
 * ios        NotSupport
 * android    NotSupport
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export async function offPageEnterForeground(
  onPageEnterForegroundCallback: onPageEnterForegroundCallback
) {
  await isAvailable('webview.offPageEnterForeground');
  unregisterSpecificNotificationHandler(
    PageEnterForegroundNotification,
    onPageEnterForegroundCallback
  );
}

const PageEnterBackgroundNotification = [
  'UnifyNotify_OnPageEnterBackground',
  'UnifyNotif_OnPageEnterBackground',
];

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * webview.onPageEnterBackground
 *
 * [PLATFORM]
 * mac        NotSupport
 * windows    NotSupport
 * linux      NotSupport
 * ios        6.4.0+
 * android    6.4.0+
 *
 * -------------------------------------------------- API Doc --------------------------------------------------
 */
export async function onPageEnterBackground(
  onPageEnterBackgroundCallback: onPageEnterBackgroundCallback
) {
  await isAvailable('webview.onPageEnterBackground');
  registerSpecificNotificationHandler(
    PageEnterBackgroundNotification,
    onPageEnterBackgroundCallback
  );
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * webview.offPageEnterBackground
 *
 * [PLATFORM]
 * mac        6.3.0+
 * windows    6.3.0+
 * linux      6.3.0+
 * ios        NotSupport
 * android    NotSupport
 *
 * -------------------------------------------------- API Doc --------------------------------------------------
 */
export async function offPageEnterBackground(
  onPageEnterBackgroundCallback: onPageEnterBackgroundCallback
) {
  await isAvailable('webview.offPageEnterBackground');
  unregisterSpecificNotificationHandler(
    PageEnterBackgroundNotification,
    onPageEnterBackgroundCallback
  );
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * webview.getPerformanceMetrics
 *
 * [PLATFORM]
 * mac        6.4.0+
 * windows    6.4.0+
 * linux      6.4.0+
 * ios        6.4.0+
 * android    6.4.0+
 *
 * -------------------------------------------------- API Doc --------------------------------------------------
 */
export async function getPerformanceMetrics(
  options?: PostMessageOption
): Promise<NativeAPIResponseMessage<{ data: string }>> {
  await isAvailable('webview.getPerformanceMetrics');
  return await postJsMessage(
    API.getPerformanceMetrics,
    {},
    { timeout: 60, ...options, needResponse: true }
  );
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * webview.recreate
 *
 * [PLATFORM]
 * mac        6.5.0
 * windows    NotSupport
 * linux      NotSupport
 * ios        6.4.5
 * android    NotSupport
 * -------------------------------------------------- API Doc --------------------------------------------------
 */
export function recreate(homeUrl: string = ''): Promise<any> {
  return isAvailable('webview.recreate').then(() => {
    return postJsMessage(API.recreate, { homeUrl });
  });
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * webview.onWebviewZoomFactorChanged
 *
 * [PLATFORM]
 * mac        6.6.0+
 * windows    6.6.0+
 * linux      6.6.0+
 * ios        NotSupport
 * android    NotSupport
 *
 * -------------------------------------------------- API Doc --------------------------------------------------
 */
export async function onWebviewZoomFactorChanged(
  onWebviewZoomFactorChangeCallback: OnWebviewZoomFactorChangeCallback
) {
  await isAvailable('webview.onWebviewZoomFactorChanged');
  registerSpecificNotificationHandler(
    'UnifyNotif_OnWebviewZoomFactorChanged',
    onWebviewZoomFactorChangeCallback
  );
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * webview.offWebviewZoomFactorChanged
 *
 * [PLATFORM]
 * mac        6.6.0+
 * windows    6.6.0+
 * linux      6.6.0+
 * ios        NotSupport
 * android    NotSupport
 *
 * -------------------------------------------------- API Doc --------------------------------------------------
 */
export async function offWebviewZoomFactorChanged(
  onWebviewZoomFactorChangeCallback: OnWebviewZoomFactorChangeCallback
) {
  await isAvailable('webview.offWebviewZoomFactorChanged');
  unregisterSpecificNotificationHandler(
    'UnifyNotif_OnWebviewZoomFactorChanged',
    onWebviewZoomFactorChangeCallback
  );
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * webview.setZoomFactor
 *
 * [PLATFORM]
 * mac        6.6.0
 * windows    6.6.0
 * linux      6.6.0
 * ios        NotSupport
 * android    NotSupport
 * -------------------------------------------------- API Doc --------------------------------------------------
 */
export async function setZoomFactor(
  zoomFactor: number
): Promise<NativeAPIResponseMessage<any>> {
  await isAvailable('webview.setZoomFactor');
  return await postJsMessage(API.setZoomFactor, { zoomFactor });
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * webview.getZoomFactor
 *
 * [PLATFORM]
 * mac        6.6.0
 * windows    6.6.0
 * linux      6.6.0
 * ios        NotSupport
 * android    NotSupport
 * -------------------------------------------------- API Doc --------------------------------------------------
 */
export async function getZoomFactor(): Promise<
  NativeAPIResponseMessage<{ zoomFactor: number }>
> {
  await isAvailable('webview.getZoomFactor');
  return await postJsMessage(API.getZoomFactor, {});
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * webview.readyForPushNotification
 *
 * [PLATFORM]
 * mac        NotSupport
 * windows    NotSupport
 * linux      NotSupport
 * ios        6.5.10+
 * android    6.5.10+
 * -------------------------------------------------- API Doc --------------------------------------------------
 */
export async function readyForPushNotification(): Promise<
  NativeAPIResponseMessage<any>
> {
  await isAvailable('webview.readyForPushNotification');
  return await postJsMessage(API.readyForPushNotification, {});
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * webview.onPushNotification
 *
 * [PLATFORM]
 * mac        NotSupport
 * windows    NotSupport
 * linux      NotSupport
 * ios        6.5.10+
 * android    6.5.10
 *
 * -------------------------------------------------- API Doc --------------------------------------------------
 */
export async function onPushNotification(
  onPushNotificationCallback: OnWebviewPushNotification
) {
  await isAvailable('webview.onPushNotification');
  registerSpecificNotificationHandler(
    'UnifyNotify_OnPushNotification',
    onPushNotificationCallback
  );
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * webview.offPushNotification
 *
 * [PLATFORM]
 * mac        NotSupport
 * windows    NotSupport
 * linux      NotSupport
 * ios        6.5.10+
 * android    6.5.10+
 *
 * -------------------------------------------------- API Doc --------------------------------------------------
 */
export async function offPushNotification(
  onPushNotificationCallback: OnWebviewPushNotification
) {
  await isAvailable('webview.offPushNotification');
  unregisterSpecificNotificationHandler(
    'UnifyNotify_OnPushNotification',
    onPushNotificationCallback
  );
}

export default {
  closeWebview,
  getWebviewId,
  isInWebview,
  onVisibilityChange,
  offVisibilityChange,
  send,
  resize,
  getRect,
  onReceiveMessage,
  offReceiveMessage,
  replyNotification,
  getPendingRequests,
  onWebviewWillRecycle,
  offWebviewWillRecycle,
  onWebviewLostFocus,
  offWebviewLostFocus,
  saveWebContent,
  restoreWebContent,
  onPageEnterBackground,
  offPageEnterBackground,
  onPageEnterForeground,
  offPageEnterForeground,
  getPerformanceMetrics,
  recreate,
  onWebviewZoomFactorChanged,
  offWebviewZoomFactorChanged,
  getZoomFactor,
  setZoomFactor,
  readyForPushNotification,
  onPushNotification,
  offPushNotification,
  queryHotkey,
  addShortcutListener,
  removeShortcutListener,
  enableHeartbeat,
  disableHeartbeat,
  getHeartbeatStatus,
  onHotkeyComboChange,
  offHotkeyComboChange,
};
